import type { ComparisonRow } from '@commissionsight/sdk';
import { formatPeriodLong } from '@commissionsight/shared';
import { type ReactNode, useDeferredValue, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../api.js';
import { PeriodPicker } from '../components/PeriodPicker.js';
import { WorkspaceFilter } from '../components/WorkspaceFilter.js';
import {
  Button,
  Card,
  Field,
  FlagChips,
  InfoTip,
  Loading,
  SortHeader,
  Spinner,
  StatPill,
  inputClass,
  useSort,
} from '../components/ui.js';
import { downloadCsv, toCsv } from '../lib/csv.js';
import { explainRow, formatCurrency } from '../lib/status.js';
import { useCarriers } from '../lib/useCarriers.js';

interface Summary {
  green: number;
  yellow: number;
  red: number;
  new: number;
  reappeared: number;
  total: number;
}

export function Compare() {
  const { client } = useAuth();
  const { carriers } = useCarriers();
  // Deep-linkable: ?carrierId=&from=&to=&workspaceId= pre-fill the comparison
  // (the Statement-quality card links here for a flagged carrier's period).
  const [searchParams] = useSearchParams();
  const [periods, setPeriods] = useState<string[]>([]);
  const [carrierId, setCarrierId] = useState(searchParams.get('carrierId') ?? '');
  const [workspaceId, setWorkspaceId] = useState(searchParams.get('workspaceId') ?? '');
  const [from, setFrom] = useState(searchParams.get('from') ?? '');
  const [to, setTo] = useState(searchParams.get('to') ?? '');
  const [rows, setRows] = useState<ComparisonRow[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [filter, setFilter] = useState<'all' | 'green' | 'yellow' | 'red'>('all');
  const [search, setSearch] = useState('');
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Defer the expensive re-filter of a (potentially 50k-row) result set so
  // typing or clicking the status filter stays responsive. `isFiltering` is a
  // REAL pending signal — true only while React is actually recomputing the
  // filtered list — not a fixed fake timer.
  const deferredSearch = useDeferredValue(search);
  const deferredFilter = useDeferredValue(filter);
  const isFiltering = search !== deferredSearch || filter !== deferredFilter;

  // Discover carriers + the periods that actually have data, and default to
  // the two most recent periods so the page is useful immediately.
  // biome-ignore lint/correctness/useExhaustiveDependencies: bootstrap once
  useEffect(() => {
    client
      .attritionSeries({ months: 24 })
      .then((r) => {
        const ps = r.data.map((d) => d.period);
        setPeriods(ps);
        if (from || to) return; // pre-filled from the URL — don't override
        if (ps.length >= 2) {
          setFrom(ps[ps.length - 2] ?? '');
          setTo(ps[ps.length - 1] ?? '');
        } else if (ps.length === 1) {
          setFrom(ps[0] ?? '');
          setTo(ps[0] ?? '');
        }
      })
      .catch(() => {});
  }, []);

  // Re-run whenever the selection changes.
  useEffect(() => {
    if (!from || !to) return;
    let active = true;
    setBusy(true);
    setError(null);
    client
      .compare({
        from,
        to,
        carrierId: carrierId || undefined,
        workspaceId: workspaceId || undefined,
      })
      .then((res) => {
        if (!active) return;
        setRows(res.data);
        setSummary(res.summary);
      })
      .catch((e) => active && setError(e instanceof Error ? e.message : 'Comparison failed'))
      .finally(() => active && setBusy(false));
    return () => {
      active = false;
    };
  }, [client, from, to, carrierId, workspaceId]);

  const setPreset = (monthsBack: number) => {
    if (periods.length === 0) return;
    const latest = periods[periods.length - 1] ?? '';
    const idx = periods.length - 1 - monthsBack;
    setTo(latest);
    setFrom(periods[idx >= 0 ? idx : 0] ?? '');
  };

  const carrierName = carrierId
    ? (carriers.find((c) => c.id === carrierId)?.name ?? '')
    : 'all carriers';
  const visible = useMemo(() => {
    const q = deferredSearch.trim().toLowerCase();
    const qAlnum = q.replace(/[^a-z0-9]/g, '');
    const matches = (r: ComparisonRow) => {
      if (!q) return true;
      const hay =
        `${r.memberName ?? ''} ${r.memberExternalId ?? ''} ${r.policyNumber ?? ''} ${r.memberRefId}`.toLowerCase();
      if (hay.includes(q)) return true;
      // ID-friendly: ignore separators so "ABC-123" matches "abc123".
      return qAlnum.length > 0 && hay.replace(/[^a-z0-9]/g, '').includes(qAlnum);
    };
    return rows.filter(
      (r) => (deferredFilter === 'all' || r.status === deferredFilter) && matches(r),
    );
  }, [rows, deferredFilter, deferredSearch]);

  // Sort the filtered set, then page it so we never render an unbounded number of
  // DOM rows (a big book can compare 15k+ members). 50/page keeps it snappy.
  const { sorted, sortKey, sortDir, toggle } = useSort(visible, {
    key: 'commissionAmount',
    dir: 'desc',
  });
  const PAGE_SIZE = 50;
  const [page, setPage] = useState(0);
  // Reset to the first page whenever the filtered/sorted set changes.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset on any view change
  useEffect(() => setPage(0), [deferredFilter, deferredSearch, sortKey, sortDir, rows]);
  const pageCount = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = sorted.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  const exportCsv = () => {
    if (exporting) return;
    setExporting(true);
    // Defer so the button's spinner paints before the (potentially large) CSV
    // is serialized on the main thread.
    setTimeout(() => {
      try {
        const csv = toCsv(rows, [
          { key: 'memberRefId', header: 'Member Ref ID' },
          { key: 'memberExternalId', header: 'Member ID' },
          { key: 'memberName', header: 'Name' },
          { key: 'policyNumber', header: 'Policy' },
          { key: 'status', header: 'Status' },
          { key: 'flags', header: 'Flags' },
          { key: 'prevCommissionAmount', header: `Commission ${from}` },
          { key: 'commissionAmount', header: `Commission ${to}` },
        ]);
        downloadCsv(`compare-${from}-vs-${to}.csv`, csv);
      } finally {
        setExporting(false);
      }
    }, 0);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Compare periods</h1>
        <p className="text-sm text-neutral-500">
          See exactly who stayed, changed, dropped, or joined between any two months.
        </p>
      </div>

      <Card>
        <div className="flex flex-col gap-4">
          <WorkspaceFilter value={workspaceId} onChange={setWorkspaceId} />
          <div className="grid items-end gap-4 md:grid-cols-3">
            <Field label="Carrier">
              <select
                className={inputClass}
                value={carrierId}
                onChange={(e) => setCarrierId(e.target.value)}
              >
                <option value="">All carriers</option>
                {carriers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="From (baseline)">
              <PeriodPicker value={from} onChange={setFrom} />
            </Field>
            <Field label="To (compared)">
              <PeriodPicker value={to} onChange={setTo} />
            </Field>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-xs text-neutral-400">Quick compare:</span>
          <Preset label="Month over month" onClick={() => setPreset(1)} />
          <Preset label="Quarter (3 mo)" onClick={() => setPreset(3)} />
          <Preset label="Year (12 mo)" onClick={() => setPreset(12)} />
        </div>
      </Card>

      {error && <p className="rounded bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>}

      {from && to && (
        <p className="text-sm text-neutral-500">
          Comparing{' '}
          <span className="font-medium text-neutral-800 dark:text-neutral-200">
            {formatPeriodLong(from)}
          </span>{' '}
          →{' '}
          <span className="font-medium text-neutral-800 dark:text-neutral-200">
            {formatPeriodLong(to)}
          </span>{' '}
          for{' '}
          <span className="font-medium text-neutral-800 dark:text-neutral-200">{carrierName}</span>
          {busy && (
            <span className="ml-2 inline-flex items-center gap-1.5 align-middle text-emerald-600">
              <Spinner className="h-3.5 w-3.5" />
            </span>
          )}
        </p>
      )}

      {busy && rows.length === 0 && (
        <Card>
          <Loading label="Crunching the comparison…" />
        </Card>
      )}

      {summary && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <Metric
            label="Evaluated"
            value={summary.total}
            info="Every member scored in this comparison — those present in either month. Retained + Changed + Dropped always equals this."
          />
          <Metric
            label="Retained"
            value={summary.green}
            tone="green"
            info="🟢 Present in both months with no tracked change. Includes first-time-seen members (counted again under New)."
          />
          <Metric
            label="Changed"
            value={summary.yellow}
            tone="yellow"
            info="🟡 Present in both months but something tracked moved — commission, a detail (plan/contact), or a reappearance."
          />
          <Metric
            label="Dropped"
            value={summary.red}
            tone="red"
            info="🔴 Present in the earlier month, absent in the later one — fallout. Drives attrition (Dropped ÷ members present the earlier month)."
          />
          <Metric
            label="New"
            value={summary.new}
            info="First time we've ever seen this member (the NEW flag). A subset of Retained — shown separately, not added on top."
          />
          <Metric
            label="Reappeared"
            value={summary.reappeared}
            info="Returned after being absent (the REAPPEARED flag) — compared against the month they were last seen. A subset of Changed."
          />
        </div>
      )}

      {rows.length > 0 && (
        <Card className="p-0">
          <div className="flex flex-wrap items-center gap-2 border-b border-neutral-200 px-5 py-3 dark:border-neutral-800">
            <div className="relative min-w-[200px] flex-1">
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
                className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400"
              >
                <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                <path d="M20 20l-3-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, member ID, or policy…"
                className="w-full rounded-lg border border-neutral-300 bg-white py-1.5 pl-8 pr-8 text-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 dark:border-neutral-700 dark:bg-neutral-950"
              />
              {isFiltering && (
                <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-emerald-600">
                  <Spinner className="h-3.5 w-3.5" />
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {(['all', 'green', 'yellow', 'red'] as const).map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFilter(f)}
                  className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium capitalize transition ${
                    filter === f
                      ? 'bg-neutral-900 text-white dark:bg-white dark:text-neutral-900'
                      : 'text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800'
                  }`}
                >
                  {f === 'all' ? 'All' : f}
                </button>
              ))}
            </div>
            <Button variant="ghost" loading={exporting} onClick={exportCsv}>
              {exporting ? 'Exporting…' : 'Export CSV'}
            </Button>
          </div>
          <div className="max-h-[55vh] overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white text-left text-xs uppercase tracking-wide text-neutral-500 dark:bg-neutral-900">
                <tr className="border-b border-neutral-200 dark:border-neutral-800">
                  <SortHeader
                    label="Member"
                    k="memberName"
                    sortKey={sortKey}
                    sortDir={sortDir}
                    onSort={toggle}
                    className="px-5 py-2"
                  />
                  <SortHeader
                    label="Status"
                    k="status"
                    sortKey={sortKey}
                    sortDir={sortDir}
                    onSort={toggle}
                    className="px-3"
                  />
                  <th className="px-3 font-medium">What changed</th>
                  <SortHeader
                    label="Commission"
                    k="commissionAmount"
                    align="right"
                    sortKey={sortKey}
                    sortDir={sortDir}
                    onSort={toggle}
                    className="px-5 text-right"
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800/60">
                {pageRows.map((row) => (
                  <tr key={row.memberRefId} className="align-top">
                    <td className="px-5 py-2.5">
                      <Link
                        to={`/members/${row.memberRefId}`}
                        className="font-medium text-emerald-700 hover:underline dark:text-emerald-400"
                        title="View this member's full history"
                      >
                        {row.memberName ??
                          row.memberExternalId ??
                          `${row.memberRefId.slice(0, 10)}…`}
                      </Link>
                      <div className="text-xs text-neutral-400">
                        {row.memberName && row.memberExternalId ? row.memberExternalId : null}
                        {row.policyNumber ? (
                          <span className="ml-2">Policy {row.policyNumber}</span>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      <StatPill status={row.status} />
                      <div className="mt-1">
                        <FlagChips flags={row.flags} />
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-xs text-neutral-500">{explainRow(row)}</td>
                    <td className="px-5 py-2.5 text-right">
                      <div className="font-medium">{formatCurrency(row.commissionAmount)}</div>
                      {row.prevCommissionAmount !== null &&
                        row.prevCommissionAmount !== row.commissionAmount && (
                          <div className="text-xs text-neutral-400">
                            was {formatCurrency(row.prevCommissionAmount)}
                          </div>
                        )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between px-5 py-2 text-xs text-neutral-400">
            <span>
              {sorted.length === 0
                ? 'No matches'
                : `${safePage * PAGE_SIZE + 1}–${Math.min((safePage + 1) * PAGE_SIZE, sorted.length)} of ${sorted.length.toLocaleString()}`}
              {sorted.length !== rows.length && ` (filtered from ${rows.length.toLocaleString()})`}
            </span>
            {pageCount > 1 && (
              <span className="flex items-center gap-1">
                <button
                  type="button"
                  disabled={safePage === 0}
                  onClick={() => setPage(safePage - 1)}
                  className="rounded border border-neutral-300 px-2 py-0.5 font-medium text-neutral-600 enabled:hover:bg-neutral-100 disabled:opacity-40 dark:border-neutral-700 dark:enabled:hover:bg-neutral-800"
                >
                  Prev
                </button>
                <span className="px-1 tabular-nums">
                  {safePage + 1} / {pageCount}
                </span>
                <button
                  type="button"
                  disabled={safePage >= pageCount - 1}
                  onClick={() => setPage(safePage + 1)}
                  className="rounded border border-neutral-300 px-2 py-0.5 font-medium text-neutral-600 enabled:hover:bg-neutral-100 disabled:opacity-40 dark:border-neutral-700 dark:enabled:hover:bg-neutral-800"
                >
                  Next
                </button>
              </span>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}

function Preset({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-full border border-neutral-300 px-3 py-1 text-xs font-medium text-neutral-600 transition hover:border-emerald-400 hover:text-emerald-700 dark:border-neutral-700 dark:text-neutral-300 dark:hover:border-emerald-600/60"
    >
      {label}
    </button>
  );
}

function Metric({
  label,
  value,
  tone,
  info,
}: {
  label: string;
  value: number;
  tone?: 'green' | 'yellow' | 'red';
  info?: ReactNode;
}) {
  const dot =
    tone === 'green'
      ? 'bg-emerald-500'
      : tone === 'yellow'
        ? 'bg-amber-400'
        : tone === 'red'
          ? 'bg-rose-500'
          : 'bg-neutral-300 dark:bg-neutral-600';
  return (
    <Card className="p-4">
      <div className="flex items-center gap-1.5 text-xs text-neutral-500">
        <span className={`h-2 w-2 rounded-full ${dot}`} />
        {label}
        {info && <InfoTip title={label}>{info}</InfoTip>}
      </div>
      <div className="mt-1 text-2xl font-semibold tabular-nums">{value.toLocaleString()}</div>
    </Card>
  );
}
